import { defineComponent as _defineComponent } from 'vue'
import { ref } from 'vue';
import type { Keyword } from '@ilteducation/content-ref-data-types';
import customProvider, { Params } from '@/data-provider/customProvider';
import { useExcelDownload } from '@/comp/use-excel-download';
import { useBabelfish } from '@/comp/use-babelfish';
import { useRouter } from 'vue-router/composables';
import { VideoV1 } from '@ilteducation/content-registry-types';


export default /*#__PURE__*/_defineComponent({
  __name: 'DownloadKeywordStats',
  setup(__props) {

const { exportExcel } = useExcelDownload();
const { lokaliseProjectId } = useBabelfish();
const router = useRouter();

const loading = ref(false);

async function fetchKeywords(): Promise<Keyword[]> {
  const params: Params = {
    filter: {},
    sort: [],
    pagination: {
      cursor: undefined,
      limit: 10000,
    },
    include: undefined,
  };

  const getData = () => customProvider().getList('keywords', params);
  const keywords: Keyword[] = [];
  let nextCursor = undefined;
  do {
    // eslint-disable-next-line no-await-in-loop
    const { data, cursor } = await getData();
    keywords.push(...(data as Keyword[]));
    params.pagination.cursor = cursor;
    nextCursor = cursor;
  } while (nextCursor);
  return keywords;
}

async function getVideoConnectedKeywordIds(): Promise<Set<string>> {
  const params: Params = {
    filter: {},
    sort: [],
    pagination: {
      perPage: 100,
      page: 1,
    },
    include: undefined,
  };

  const getData = () => customProvider().getList('videos', params);
  let videosNumber = 0;
  const keywordIdSet = new Set<string>();
  let done = true;
  do {
    // eslint-disable-next-line no-await-in-loop
    const { data, total } = await getData();
    (data as VideoV1[]).forEach((video) => {
      video.keywords.forEach((id) => keywordIdSet.add(id));
    });
    videosNumber += data?.length ?? 0;
    done = videosNumber >= (total ?? 0);
    params.pagination.page! += 1;
  } while (!done);
  return keywordIdSet;
}
function chunkArray<T>(array: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

async function fetchImagesInChunks(imageIds: string[]): Promise<any[]> {
  const chunks = chunkArray(imageIds, 200);
  const images: any[] = [];
  for (const chunk of chunks) {
    const res = await customProvider().getList('content-image', {
      filter: { id: chunk },
      pagination: { perPage: chunk.length, page: 1 },
      sort: [],
    });
    images.push(...(res.data ?? []));
  }
  return images;
}

async function createExcel() {
  try {
    loading.value = true;
    const keywords = await fetchKeywords();
    const imageIds = keywords.map((k) => k.imageId).filter((id) => id) as string[];
    const images = await fetchImagesInChunks(imageIds);
    const videoConnectedKeywordIds = await getVideoConnectedKeywordIds();

    const altText = (keyword: Keyword) => {
      if (!keyword.imageId) {
        return 'no';
      }
      const image = images?.find((img) => img.id === keyword.imageId);
      if (!image) {
        return 'no';
      }
      if (!image.altText) {
        return 'no';
      }
      return image.language
        ? `https://app.lokalise.com/project/${lokaliseProjectId.value}/?view=multi&search=${image.id}::altText`
        : location.origin +
            router.resolve({
              name: 'content-image_edit',
              params: { id: image.id },
            }).href;
    };

    // Check video connections and convert keywords in a single pass
    const convertedKeywords = keywords.map((keyword) => ({
      ...keyword,
      hasVideo: videoConnectedKeywordIds.has(keyword.id) ? 'yes' : 'no',
      hasImage: keyword.imageId ? 'yes' : 'no',
      altText: altText(keyword),
      active: keyword.active ? 'yes' : 'no',
    }));

    exportExcel(
      [
        { label: 'Id', field: 'id' },
        { label: 'Key', field: 'key' },
        { label: 'Has image', field: 'hasImage' },
        { label: 'Image alt text', field: 'altText' },
        { label: 'Has connected video', field: 'hasVideo' },
        { label: 'Active', field: 'active' },
      ],
      convertedKeywords,
      'keywords',
    );
  } finally {
    loading.value = false;
  }
}

return { __sfc: true,exportExcel, lokaliseProjectId, router, loading, fetchKeywords, getVideoConnectedKeywordIds, chunkArray, fetchImagesInChunks, createExcel }
}

})